import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import PixKeyInput from 'components/PixKeyInput';
import useAuth from 'contexts/useAuth';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getAccountPixDefault, saveAccountPixDefault } from 'services/api.service';
import { validCpf, validCnpj } from 'services/validDocument';
import { maskDocument } from 'utils/document';
import { onlyNumbers } from 'utils/number';
import { maskPixKey, unMaskPixKey } from 'utils/pix';

const ConfigureAccount = ({ showEveryDay = false }: { showEveryDay?: boolean }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [typePix, setTypePix] = useState<'CPF' | 'PHONE' | null>(null);
  const toast = useToast();
  const { userData } = useAuth();

  const { control, handleSubmit, setValue, reset } = useForm<{
    keyPix: string;
    document: string;
    name: string;
  }>();

  const { data: accountPixDefault } = useQuery({
    queryKey: ['accountPixDefault'],
    queryFn: () => getAccountPixDefault(),
    refetchOnWindowFocus: false,
    enabled: userData?.isAuth === true,
  });

  useEffect(() => {
    if (isOpen) {
      const account = accountPixDefault?.CustomerAccounts;
      if (account) {
        const pixKey = account?.pix;
        setTypePix(pixKey ? (maskPixKey(pixKey).type as 'CPF' | 'PHONE') : null);
        reset({
          keyPix: pixKey ? maskPixKey(pixKey).masked : '',
          document: maskDocument(account?.accountHolderDocument),
          name: account?.accountHolder,
        });
      } else {
        reset();
      }
    }
  }, [isOpen, accountPixDefault, reset]);

  const onSubmit = (data: any) => {
    const pix = unMaskPixKey(data.keyPix, typePix);
    saveAccountPixDefault({
      pixKey: pix.raw,
      document: onlyNumbers(data.document),
      name: data.name,
    })
      .then(() => {
        toast({
          title: 'Conta configurada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
      })
      .catch((err) => {
        toast({
          title: 'Erro ao configurar conta',
          description: err.response.data.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      {(showEveryDay || !accountPixDefault?.CustomerAccounts) && (
        <CustomButton
          variant="solid"
          onClick={() => {
            onOpen();
          }}
        >
          Configurar Pix para recebimento
        </CustomButton>
      )}
      {isOpen && (
        <Drawer
          isOpen={isOpen}
          placement="bottom"
          onClose={onClose}
        >
          <DrawerOverlay />
          <DrawerContent pb="var(--safe-area-inset-bottom)">
            <DrawerCloseButton />

            <DrawerBody>
              <VStack
                spacing={4}
                mb={10}
              >
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                >
                  Configuração da conta
                </Text>
                <PixKeyInput
                  label="Chave Pix"
                  control={control}
                  placeholder="Chave pix"
                  name="keyPix"
                  type="text"
                  setPixTypeKey={setTypePix}
                  registerOptions={{
                    required: 'Campo obrigatório',
                    validate: {
                      valid: (value) => {
                        return unMaskPixKey(value).type !== 'INVALID' || 'Chave pix inválida';
                      },
                    },
                  }}
                  setValue={setValue}
                />

                <InputForm
                  control={control}
                  label="Documento recebedor"
                  placeholder="CPF ou CNPJ"
                  name="document"
                  type="text"
                  rules={{
                    required: 'Campo obrigatório',
                    validate: {
                      valid: (value) => {
                        value = onlyNumbers(value);

                        return validCpf(value) || validCnpj(value) || 'Documento inválido';
                      },
                    },
                  }}
                />
                <InputForm
                  control={control}
                  placeholder="Nome do recebedor"
                  label="Nome do recebedor"
                  name="name"
                  type="text"
                />
                <CustomButton onClick={handleSubmit(onSubmit)}>Salvar</CustomButton>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default ConfigureAccount;
